type MediaEndedListenerEntry = {
  video: HTMLVideoElement;
  listener: () => void;
};

type VisualMediaElement = HTMLImageElement | HTMLVideoElement;

type MediaReadyListenerEntry = {
  media_element: VisualMediaElement;
  listeners: Array<{
    type: string;
    listener: () => void;
  }>;
  timer_id: number | null;
};

type VideoControlsPreparationListenerEntry = {
  type: string;
  listener: EventListener;
  capture: boolean;
};

type VideoControlsPreparationEntry = {
  video: HTMLVideoElement;
  original_controls: boolean;
  original_tab_index: string | null;
  original_aria_label: string | null;
  listeners: VideoControlsPreparationListenerEntry[];
};

type VideoThumbnailPreparationEntry = {
  video: HTMLVideoElement;
  original_preload: string | null;
  original_current_time: number;
  listeners: Array<{
    type: string;
    listener: EventListener;
  }>;
};

/**
 * // Class Obj: モーダル内メディアの再生状態を管理
 * // Note:
 * // - 描画済みの要素内にある video を自動再生します。
 * // - ブラウザの自動再生制限に配慮し、muted / playsInline / autoplay を補完します。
 * // - モーダルを閉じる前に video を停止できるようにします。
 * // - Safariで動画サムネイルが空白にならないよう、open要素内の先頭フレーム表示を補助します。
 */
export class MediaPlaybackController
{
  private static readonly VISUAL_MEDIA_LAYOUT_READY_TIMEOUT = 700;
  private static readonly VIDEO_THUMBNAIL_SEEK_TIME = 0.001;
  private static readonly ATTR_VIDEO_STATE = 'data-modal-video-state';
  private static readonly DEFAULT_VIDEO_CONTROLS_ARIA_LABEL = '動画のコントロールを表示';

  private readonly mEndedListenerEntries: MediaEndedListenerEntry[] = [];
  private readonly mReadyListenerEntries: MediaReadyListenerEntry[] = [];
  private readonly mVideoControlsPreparationEntries: VideoControlsPreparationEntry[] = [];
  private readonly mVideoThumbnailPreparationEntries: VideoThumbnailPreparationEntry[] = [];

  /**
   * // Method: 指定要素内の video を自動再生する
   * // Note:
   * // - should_play が false の場合は、描画だけ行い再生処理は行いません。
   */
  public playVideosInElement(element: HTMLElement, should_play: boolean = true): void
  {
    if (!should_play)
    {
      return;
    }

    const videos: HTMLVideoElement[] = this.getVideosInElement(element);

    videos.forEach((video: HTMLVideoElement) =>
    {
      this.playVideo(video);
    });
  }

  /**
   * // Method: 指定要素内の video を停止する
   * // Note:
   * // - should_reset_position が true の場合は、再生位置を先頭へ戻します。
   * // - controls待機中のeventと一時属性は解除しますが、切替直前にcontrolsは表示しません。
   */
  public stopVideosInElement(element: HTMLElement, should_reset_position: boolean = true): void
  {
    const videos: HTMLVideoElement[] = this.getVideosInElement(element);

    videos.forEach((video: HTMLVideoElement) =>
    {
      this.cancelPreparedVideoControls(video, false);
      video.pause();

      if (should_reset_position)
      {
        video.currentTime = 0;
      }
    });
  }

  /**
   * // Method: open要素内のvideoをSafariでもサムネイル表示できる状態へ整える
   * // Note:
   * // - posterが指定されているvideoは既存表示を優先します。
   * // - posterがない場合だけmetadataを読み込み、先頭直後へseekして最初のフレーム描画を促します。
   */
  public prepareVideoThumbnailsInElements(elements: HTMLElement[]): void
  {
    this.clearVideoThumbnailPreparationEntries(true);

    const videos: HTMLVideoElement[] = this.getUniqueVideosInElements(elements);

    videos.forEach((video: HTMLVideoElement) =>
    {
      this.prepareVideoThumbnail(video);
    });
  }

  /**
   * // Method: 登録済みeventと一時状態を解除する
   */
  public destroy(): void
  {
    this.clearEndedListeners();
    this.clearReadyListeners();
    this.cancelAllPreparedVideoControls(true);
    this.clearVideoThumbnailPreparationEntries(true);
  }

  /**
   * // Method: 登録済み video ended event を解除する
   * // Note:
   * // - video 終了待ち中に別 content へ切り替わった場合、古い video の終了で現在のUI状態を更新しないようにします。
   */
  public clearVideoEndedListeners(): void
  {
    this.clearEndedListeners();
  }

  /**
   * // Method: 指定要素内の画像・video がレイアウト計算できる状態になるまで待機する
   * // Note:
   * // - content switch fade-in 前に自然サイズを確定し、縮みやサイズ変化が見えるのを防ぎます。
   * // - 読み込みが遅い場合でも切り替えが止まらないよう、短い fallback timeout で完了扱いにします。
   */
  public waitVisualMediaLayoutReadyInElement(element: HTMLElement): Promise<void>
  {
    const visual_media_elements: VisualMediaElement[] = this.getVisualMediaInElement(element);

    if (visual_media_elements.length === 0)
    {
      return Promise.resolve();
    }

    return Promise.all(
      visual_media_elements.map((visual_media_element: VisualMediaElement): Promise<void> =>
      {
        return this.waitVisualMediaLayoutReady(visual_media_element);
      }),
    ).then((): void => undefined);
  }

  /**
   * // Method: 指定要素内の video がレイアウト計算できる状態になるまで待機する
   */
  public waitVideosLayoutReadyInElement(element: HTMLElement): Promise<void>
  {
    const videos: HTMLVideoElement[] = this.getVideosInElement(element);

    if (videos.length === 0)
    {
      return Promise.resolve();
    }

    return Promise.all(
      videos.map((video: HTMLVideoElement): Promise<void> =>
      {
        return this.waitVisualMediaLayoutReady(video);
      }),
    ).then((): void => undefined);
  }

  /**
   * // Method: DOM挿入前のvideoへcontrols抑制と即時解除eventを設定する
   * // Note:
   * // - cloneをDOMへ挿入する前にcontrols属性を外し、SafariのネイティブUI先行描画を防ぎます。
   * // - pointerdown / touchstartを先に受けるため、表示直後から待ち時間なしでcontrolsを有効化できます。
   */
  public prepareVideoControlsInNode(node: Node): void
  {
    const videos: HTMLVideoElement[] = this.getVideosInNode(node);

    videos.forEach((video: HTMLVideoElement) =>
    {
      this.prepareVideoControls(video);
    });
  }

  /**
   * // Method: 描画済み要素内のvideoへcontrols抑制と即時解除eventを設定する
   */
  public prepareVideoControlsInElement(element: HTMLElement): void
  {
    this.prepareVideoControlsInNode(element);
  }

  /**
   * // Method: 指定要素内のcontrols操作待ち状態を明示的に解除する
   * // Note:
   * // - 通常のモーダル処理では呼ばず、ユーザー操作または再生終了時の解除を優先します。
   */
  public releaseVideoControlsInElement(element: HTMLElement): void
  {
    const videos: HTMLVideoElement[] = this.getVideosInElement(element);

    videos.forEach((video: HTMLVideoElement) =>
    {
      this.releasePreparedVideoControls(video, false);
    });
  }

  /**
   * // Method: 指定要素内の画像・video 一覧を取得する
   */
  private getVisualMediaInElement(element: HTMLElement): VisualMediaElement[]
  {
    if (element instanceof HTMLImageElement || element instanceof HTMLVideoElement)
    {
      return [element];
    }

    return Array.from(
      element.querySelectorAll<VisualMediaElement>('img, video'),
    );
  }

  /**
   * // Method: 指定Node内のvideo一覧を取得する
   */
  private getVideosInNode(node: Node): HTMLVideoElement[]
  {
    if (node instanceof HTMLVideoElement)
    {
      return [node];
    }

    if (!(node instanceof Element || node instanceof DocumentFragment))
    {
      return [];
    }

    return Array.from(node.querySelectorAll<HTMLVideoElement>('video'));
  }

  /**
   * // Method: 指定要素内の video 一覧を取得する
   */
  private getVideosInElement(element: HTMLElement): HTMLVideoElement[]
  {
    return this.getVideosInNode(element);
  }

  /**
   * // Method: 複数要素内のvideoを重複なしで取得する
   */
  private getUniqueVideosInElements(elements: HTMLElement[]): HTMLVideoElement[]
  {
    const videos: HTMLVideoElement[] = [];

    elements.forEach((element: HTMLElement) =>
    {
      videos.push(...this.getVideosInElement(element));
    });

    return Array.from(new Set(videos));
  }

  /**
   * // Method: video の自動再生を試みる
   * // Note:
   * // - Safariがsource読込前からinline再生条件を認識できるよう、propertyとattributeを両方同期します。
   * // - DOM挿入時のautoplay属性による先行再生は避け、挿入後のplay()へ一本化します。
   * // - 自動再生が拒否されてもcontrolsは自動表示せず、登録済みのユーザー操作待ちを維持します。
   */
  private playVideo(video: HTMLVideoElement): void
  {
    this.applyInlinePlaybackAttributes(video);
    video.autoplay = false;
    video.removeAttribute('autoplay');

    const play_promise: Promise<void> | undefined = video.play();

    if (!play_promise)
    {
      return;
    }

    play_promise.catch(() =>
    {
      // Keep: autoplay拒否を理由にcontrols=trueへ戻すと、Safariで表示直後にUIが出るため操作待ちを維持します。
    });
  }

  /**
   * // Method: Safari向けinline再生属性を同期する
   */
  private applyInlinePlaybackAttributes(video: HTMLVideoElement): void
  {
    video.setAttribute('controlslist', 'nodownload');
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
  }

  /**
   * // Method: 指定要素内に video があるか判定する
   */
  public hasVideosInElement(element: HTMLElement): boolean
  {
    return this.getVideosInElement(element).length > 0;
  }

  /**
   * // Method: 画像・video がサイズ計算に使える状態か判定する
   */
  private isVisualMediaLayoutReady(visual_media_element: VisualMediaElement): boolean
  {
    if (visual_media_element instanceof HTMLVideoElement)
    {
      return (
        visual_media_element.readyState >= HTMLMediaElement.HAVE_METADATA &&
        visual_media_element.videoWidth > 0 &&
        visual_media_element.videoHeight > 0
      );
    }

    return (
      visual_media_element.complete &&
      visual_media_element.naturalWidth > 0 &&
      visual_media_element.naturalHeight > 0
    );
  }

  /**
   * // Method: 1つの画像・video がレイアウト計算できる状態になるまで待機する
   */
  private waitVisualMediaLayoutReady(visual_media_element: VisualMediaElement): Promise<void>
  {
    if (this.isVisualMediaLayoutReady(visual_media_element))
    {
      return Promise.resolve();
    }

    return new Promise((resolve: () => void): void =>
    {
      let is_finished = false;

      const finish = (): void =>
      {
        if (is_finished)
        {
          return;
        }

        is_finished = true;
        this.removeReadyListenerEntry(ready_listener_entry);
        resolve();
      };

      const ready_listener_entry: MediaReadyListenerEntry = {
        media_element: visual_media_element,
        listeners: this.createVisualMediaReadyListeners(visual_media_element, finish),
        timer_id: window.setTimeout(
          finish,
          MediaPlaybackController.VISUAL_MEDIA_LAYOUT_READY_TIMEOUT,
        ),
      };

      ready_listener_entry.listeners.forEach((listener_entry) =>
      {
        visual_media_element.addEventListener(
          listener_entry.type,
          listener_entry.listener,
          { once: true },
        );
      });

      this.mReadyListenerEntries.push(ready_listener_entry);

      if (this.isVisualMediaLayoutReady(visual_media_element))
      {
        finish();
      }
    });
  }

  /**
   * // Method: 画像・video の準備完了待ち event を作成する
   */
  private createVisualMediaReadyListeners(
    visual_media_element: VisualMediaElement,
    listener: () => void,
  ): Array<{ type: string; listener: () => void }>
  {
    if (visual_media_element instanceof HTMLVideoElement)
    {
      return [
        { type: 'loadedmetadata', listener },
        { type: 'loadeddata', listener },
        { type: 'canplay', listener },
        { type: 'error', listener },
      ];
    }

    return [
      { type: 'load', listener },
      { type: 'error', listener },
    ];
  }

  /**
   * // Method: 指定要素内に再生中の video があるか判定する
   */
  public hasPlayingVideosInElement(element: HTMLElement): boolean
  {
    const videos: HTMLVideoElement[] = this.getVideosInElement(element);

    return videos.some((video: HTMLVideoElement) =>
    {
      return !video.paused && !video.ended;
    });
  }

  /**
   * // Method: 指定要素内の video 再生終了時に処理を実行する
   */
  public bindVideosEndedInElement(
    element: HTMLElement,
    callback: () => void,
  ): void
  {
    const videos: HTMLVideoElement[] = this.getVideosInElement(element);

    if (videos.length === 0)
    {
      callback();
      return;
    }

    let remaining_count: number = videos.filter((video: HTMLVideoElement) =>
    {
      return !video.ended;
    }).length;

    if (remaining_count === 0)
    {
      callback();
      return;
    }

    videos.forEach((video: HTMLVideoElement) =>
    {
      if (video.ended)
      {
        return;
      }

      const ended_listener = (): void =>
      {
        this.removeEndedListener(video, ended_listener);
        remaining_count -= 1;

        if (remaining_count <= 0)
        {
          callback();
        }
      };

      video.addEventListener('ended', ended_listener, { once: true });
      this.mEndedListenerEntries.push({
        video,
        listener: ended_listener,
      });
    });
  }

  /**
   * // Method: 1つのvideoへcontrols抑制状態と即時解除eventを設定する
   */
  private prepareVideoControls(video: HTMLVideoElement): void
  {
    this.cancelPreparedVideoControls(video, false);

    const original_controls: boolean = video.controls;
    const original_tab_index: string | null = video.getAttribute('tabindex');
    const original_aria_label: string | null = video.getAttribute('aria-label');

    this.applyInlinePlaybackAttributes(video);
    video.autoplay = false;
    video.removeAttribute('autoplay');
    video.controls = false;
    video.removeAttribute('controls');
    video.setAttribute(MediaPlaybackController.ATTR_VIDEO_STATE, 'awaiting-interaction');

    if (!video.hasAttribute('tabindex'))
    {
      video.setAttribute('tabindex', '0');
    }

    if (!video.hasAttribute('aria-label'))
    {
      video.setAttribute(
        'aria-label',
        MediaPlaybackController.DEFAULT_VIDEO_CONTROLS_ARIA_LABEL,
      );
    }

    const release_by_interaction: EventListener = (event: Event): void =>
    {
      if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ')
      {
        return;
      }

      this.releasePreparedVideoControls(video, false);
    };

    const release_and_refresh: EventListener = (): void =>
    {
      this.releasePreparedVideoControls(video, true);
    };

    const listeners: VideoControlsPreparationListenerEntry[] = [
      { type: 'pointerdown', listener: release_by_interaction, capture: true },
      { type: 'touchstart', listener: release_by_interaction, capture: true },
      { type: 'click', listener: release_by_interaction, capture: true },
      { type: 'keydown', listener: release_by_interaction, capture: true },
      { type: 'ended', listener: release_and_refresh, capture: false },
    ];

    listeners.forEach((listener_entry: VideoControlsPreparationListenerEntry) =>
    {
      video.addEventListener(
        listener_entry.type,
        listener_entry.listener,
        { capture: listener_entry.capture },
      );
    });

    this.mVideoControlsPreparationEntries.push({
      video,
      original_controls,
      original_tab_index,
      original_aria_label,
      listeners,
    });
  }

  /**
   * // Method: 1つのvideoのcontrols抑制状態を解除する
   * // Note:
   * // - ユーザー操作時は同一gesture内でcontrols=trueへ切り替えます。
   * // - ended時はcontrols属性を再設定し、SafariのネイティブUI再描画を促します。
   */
  private releasePreparedVideoControls(
    video: HTMLVideoElement,
    should_refresh_native_controls: boolean,
  ): void
  {
    const preparation_entry: VideoControlsPreparationEntry | null =
      this.removeVideoControlsPreparationEntry(video);

    if (!preparation_entry)
    {
      return;
    }

    this.restoreTemporaryVideoAccessibilityAttributes(preparation_entry);
    video.removeAttribute(MediaPlaybackController.ATTR_VIDEO_STATE);

    if (should_refresh_native_controls)
    {
      video.controls = false;
      video.removeAttribute('controls');
      video.getBoundingClientRect();
    }

    video.controls = true;
    video.setAttribute('controls', '');
  }

  /**
   * // Method: controls抑制状態を表示復帰せず解除する
   */
  private cancelPreparedVideoControls(
    video: HTMLVideoElement,
    should_restore_original_controls: boolean,
  ): void
  {
    const preparation_entry: VideoControlsPreparationEntry | null =
      this.removeVideoControlsPreparationEntry(video);

    if (!preparation_entry)
    {
      return;
    }

    this.restoreTemporaryVideoAccessibilityAttributes(preparation_entry);
    video.removeAttribute(MediaPlaybackController.ATTR_VIDEO_STATE);

    if (!should_restore_original_controls)
    {
      return;
    }

    video.controls = preparation_entry.original_controls;

    if (!preparation_entry.original_controls)
    {
      video.removeAttribute('controls');
    }
  }

  /**
   * // Method: 指定videoのcontrols準備entryを解除して返す
   */
  private removeVideoControlsPreparationEntry(
    video: HTMLVideoElement,
  ): VideoControlsPreparationEntry | null
  {
    const entry_index: number = this.mVideoControlsPreparationEntries.findIndex(
      (entry: VideoControlsPreparationEntry): boolean =>
      {
        return entry.video === video;
      },
    );

    if (entry_index < 0)
    {
      return null;
    }

    const preparation_entry: VideoControlsPreparationEntry | undefined =
      this.mVideoControlsPreparationEntries[entry_index];

    if (!preparation_entry)
    {
      return null;
    }

    preparation_entry.listeners.forEach((listener_entry: VideoControlsPreparationListenerEntry) =>
    {
      video.removeEventListener(
        listener_entry.type,
        listener_entry.listener,
        { capture: listener_entry.capture },
      );
    });
    preparation_entry.listeners.length = 0;

    this.mVideoControlsPreparationEntries.splice(entry_index, 1);

    return preparation_entry;
  }

  /**
   * // Method: controls待機中に付与したtabindex / aria-labelを復元する
   */
  private restoreTemporaryVideoAccessibilityAttributes(
    preparation_entry: VideoControlsPreparationEntry,
  ): void
  {
    if (preparation_entry.original_tab_index === null)
    {
      preparation_entry.video.removeAttribute('tabindex');
    }
    else
    {
      preparation_entry.video.setAttribute(
        'tabindex',
        preparation_entry.original_tab_index,
      );
    }

    if (preparation_entry.original_aria_label === null)
    {
      preparation_entry.video.removeAttribute('aria-label');
    }
    else
    {
      preparation_entry.video.setAttribute(
        'aria-label',
        preparation_entry.original_aria_label,
      );
    }
  }

  /**
   * // Method: controls抑制中の全videoを解除する
   */
  private cancelAllPreparedVideoControls(should_restore_original_controls: boolean): void
  {
    const videos: HTMLVideoElement[] = this.mVideoControlsPreparationEntries.map(
      (entry: VideoControlsPreparationEntry): HTMLVideoElement =>
      {
        return entry.video;
      },
    );

    videos.forEach((video: HTMLVideoElement) =>
    {
      this.cancelPreparedVideoControls(video, should_restore_original_controls);
    });
  }

  /**
   * // Method: posterなしvideoの先頭フレーム表示を準備する
   */
  private prepareVideoThumbnail(video: HTMLVideoElement): void
  {
    const poster_value: string | null = video.getAttribute('poster');

    if (poster_value?.trim())
    {
      return;
    }

    const original_preload: string | null = video.getAttribute('preload');
    const original_current_time: number = video.currentTime;

    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    if (!original_preload || original_preload === 'none')
    {
      video.preload = 'metadata';
    }

    const request_frame_listener: EventListener = (): void =>
    {
      this.requestVideoThumbnailFrame(video);
    };

    const listeners: Array<{ type: string; listener: EventListener }> = [
      { type: 'loadedmetadata', listener: request_frame_listener },
      { type: 'loadeddata', listener: request_frame_listener },
    ];

    listeners.forEach((listener_entry) =>
    {
      video.addEventListener(listener_entry.type, listener_entry.listener);
    });

    this.mVideoThumbnailPreparationEntries.push({
      video,
      original_preload,
      original_current_time,
      listeners,
    });

    this.requestVideoThumbnailFrame(video);

    if (video.readyState === HTMLMediaElement.HAVE_NOTHING && video.paused)
    {
      video.load();
    }
  }

  /**
   * // Method: Safariへvideoサムネイルの最初のフレーム描画を促す
   */
  private requestVideoThumbnailFrame(video: HTMLVideoElement): void
  {
    if (
      !video.paused ||
      video.readyState < HTMLMediaElement.HAVE_METADATA ||
      video.currentTime > 0
    )
    {
      return;
    }

    const duration: number = video.duration;
    const seek_time: number = Number.isFinite(duration) && duration > 0
      ? Math.min(MediaPlaybackController.VIDEO_THUMBNAIL_SEEK_TIME, duration / 2)
      : MediaPlaybackController.VIDEO_THUMBNAIL_SEEK_TIME;

    try
    {
      video.currentTime = seek_time;
    }
    catch
    {
      // metadata読込直後でもseekできないブラウザでは、loadeddata時の再試行へ任せます。
    }
  }

  /**
   * // Method: videoサムネイル準備eventを解除し、必要に応じて元状態へ戻す
   */
  private clearVideoThumbnailPreparationEntries(should_restore_state: boolean): void
  {
    this.mVideoThumbnailPreparationEntries.forEach((entry: VideoThumbnailPreparationEntry) =>
    {
      entry.listeners.forEach((listener_entry) =>
      {
        entry.video.removeEventListener(listener_entry.type, listener_entry.listener);
      });

      if (!should_restore_state)
      {
        return;
      }

      if (entry.original_preload === null)
      {
        entry.video.removeAttribute('preload');
      }
      else
      {
        entry.video.setAttribute('preload', entry.original_preload);
      }

      try
      {
        entry.video.currentTime = entry.original_current_time;
      }
      catch
      {
        // source解除済みなどで復元できない場合は、eventと属性の復元を優先します。
      }
    });

    this.mVideoThumbnailPreparationEntries.length = 0;
  }

  /**
   * // Method: 指定 video の ended event を管理リストから解除する
   */
  private removeEndedListener(video: HTMLVideoElement, listener: () => void): void
  {
    video.removeEventListener('ended', listener);

    const listener_index: number = this.mEndedListenerEntries.findIndex(
      (entry: MediaEndedListenerEntry): boolean =>
      {
        return entry.video === video && entry.listener === listener;
      },
    );

    if (listener_index < 0)
    {
      return;
    }

    this.mEndedListenerEntries.splice(listener_index, 1);
  }

  /**
   * // Method: 管理中の video ended event をすべて解除する
   */
  private clearEndedListeners(): void
  {
    this.mEndedListenerEntries.forEach((entry: MediaEndedListenerEntry) =>
    {
      entry.video.removeEventListener('ended', entry.listener);
    });

    this.mEndedListenerEntries.length = 0;
  }

  /**
   * // Method: 指定画像・video のレイアウト準備待ち event を管理リストから解除する
   */
  private removeReadyListenerEntry(entry: MediaReadyListenerEntry): void
  {
    entry.listeners.forEach((listener_entry) =>
    {
      entry.media_element.removeEventListener(listener_entry.type, listener_entry.listener);
    });

    if (entry.timer_id !== null)
    {
      window.clearTimeout(entry.timer_id);
      entry.timer_id = null;
    }

    const listener_index: number = this.mReadyListenerEntries.indexOf(entry);

    if (listener_index < 0)
    {
      return;
    }

    this.mReadyListenerEntries.splice(listener_index, 1);
  }

  /**
   * // Method: 管理中の画像・video レイアウト準備待ち event をすべて解除する
   */
  private clearReadyListeners(): void
  {
    [...this.mReadyListenerEntries].forEach((entry: MediaReadyListenerEntry) =>
    {
      this.removeReadyListenerEntry(entry);
    });
  }
}
